import { useEffect } from "react";
import "@/App.css";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ServicesPage from "@/pages/ServicesPage";
import PharmaEngineeringPage from "@/pages/PharmaEngineeringPage";
import SustainabilityPage from "@/pages/SustainabilityPage";
import FinanceLoansPage from "@/pages/FinanceLoansPage";
import CapitalMarketsPage from "@/pages/CapitalMarketsPage";
import MergersAcquisitionsPage from "@/pages/MergersAcquisitionsPage";
import InsightsPage from "@/pages/InsightsPage";
import CaseStudiesPage from "@/pages/CaseStudiesPage";
import ContactPage from "@/pages/ContactPage";
import CareersPage from "@/pages/CareersPage";
import InsightArticlePage from "@/pages/InsightArticlePage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfUsePage from "@/pages/TermsOfUsePage";

const SUPPORTED_LOCALES = ['en-in', 'en-us', 'en-gb', 'en-nl', 'en-ae'];
const DEFAULT_LOCALE = 'en-in';

// Locale wrapper component
const LocaleWrapper = ({ children }) => {
  const { locale } = useParams();
  const navigate = useNavigate();
  
  useEffect(() => {
    // If locale is not supported, redirect to default
    if (!SUPPORTED_LOCALES.includes(locale)) {
      navigate(`/${DEFAULT_LOCALE}`, { replace: true });
      return;
    }
    
    // Store the active locale
    localStorage.setItem('hg-locale', locale);
  }, [locale, navigate]);
  
  if (!SUPPORTED_LOCALES.includes(locale)) {
    return null;
  }
  
  return children;
};

// Layout component that includes Navbar and Footer
const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Root — renders same content as /en-in (default locale).
            Google will see real content at both / and /en-in. */}
        <Route path="/" element={<LocaleWrapper><Layout><HomePage /></Layout></LocaleWrapper>} />
        
        {/* Locale-prefixed routes */}
        <Route path="/:locale" element={<LocaleWrapper><Layout><HomePage /></Layout></LocaleWrapper>} />
        <Route path="/:locale/about" element={<LocaleWrapper><Layout><AboutPage /></Layout></LocaleWrapper>} />
        <Route path="/:locale/services" element={<LocaleWrapper><Layout><ServicesPage /></Layout></LocaleWrapper>} />
        <Route path="/:locale/services/pharma-engineering" element={<LocaleWrapper><Layout><PharmaEngineeringPage /></Layout></LocaleWrapper>} />
        <Route path="/:locale/services/sustainability" element={<LocaleWrapper><Layout><SustainabilityPage /></Layout></LocaleWrapper>} />
        <Route path="/:locale/services/finance-loans" element={<LocaleWrapper><Layout><FinanceLoansPage /></Layout></LocaleWrapper>} />
        <Route path="/:locale/services/capital-markets" element={<LocaleWrapper><Layout><CapitalMarketsPage /></Layout></LocaleWrapper>} />
        <Route path="/:locale/services/mergers-acquisitions" element={<LocaleWrapper><Layout><MergersAcquisitionsPage /></Layout></LocaleWrapper>} />
        <Route path="/:locale/insights" element={<LocaleWrapper><Layout><InsightsPage /></Layout></LocaleWrapper>} />
        <Route path="/:locale/insights/:slug" element={<LocaleWrapper><Layout><InsightArticlePage /></Layout></LocaleWrapper>} />
        <Route path="/:locale/case-studies" element={<LocaleWrapper><Layout><CaseStudiesPage /></Layout></LocaleWrapper>} />
        <Route path="/:locale/contact" element={<LocaleWrapper><Layout><ContactPage /></Layout></LocaleWrapper>} />
        <Route path="/:locale/careers" element={<LocaleWrapper><Layout><CareersPage /></Layout></LocaleWrapper>} />
        <Route path="/:locale/privacy-policy" element={<LocaleWrapper><Layout><PrivacyPolicyPage /></Layout></LocaleWrapper>} />
        <Route path="/:locale/terms-of-use" element={<LocaleWrapper><Layout><TermsOfUsePage /></Layout></LocaleWrapper>} />
        
        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to={`/${DEFAULT_LOCALE}`} replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
